import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { brand, gray } from "../../../../../theme/themePrimitives";
import { UseBulkUserImportReturn } from "../../useBulkUserImport";
import { CsvUpload } from "./CsvUpload";

type PrepareAndUploadProps = Pick<
  UseBulkUserImportReturn,
  | "file"
  | "setFile"
  | "jobId"
  | "hasRunValidation"
  | "uploadFile"
  | "runValidation"
  | "isUploading"
  | "isValidating"
  | "awaitingValidation"
  | "downloadTemplate"
  | "downloadingTemplate"
>;

export const PrepareAndUpload: React.FC<PrepareAndUploadProps> = ({
  file,
  setFile,
  jobId,
  hasRunValidation,
  uploadFile,
  runValidation,
  isUploading,
  isValidating,
  awaitingValidation,
  downloadTemplate,
  downloadingTemplate,
}) => {
  const handleFileChange = (selected: File | null) => {
    if (!selected) {
      setFile(null);
      return;
    }
    void uploadFile(selected);
  };

  return (
    <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
      <Box p={3} sx={{ display: "flex", gap: 3 }}>
        <Box flex={1}>
          <Typography variant="h4">Template Download</Typography>
          <Stack p={3} spacing={2}>
            <Stack spacing={1}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box
                  sx={{
                    backgroundColor: brand[600],
                    borderRadius: "999px",
                    width: 25,
                    height: 25,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Typography color="#FFFFFF">1</Typography>
                </Box>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Download template
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Download one of the provided templates.
                  </Typography>
                </Stack>
              </Box>
              <Box
                onClick={() =>
                  downloadingTemplate === null && downloadTemplate(false)
                }
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  border: `1px solid ${gray[200]}`,
                  cursor: "pointer",
                  "&:hover": { bgcolor: gray[50] },
                }}
              >
                <Typography variant="subtitle2">
                  Download blank template
                </Typography>
                {downloadingTemplate === "blank" ? (
                  <CircularProgress size={18} sx={{ color: gray[500] }} />
                ) : (
                  <FileDownloadOutlinedIcon sx={{ color: gray[500] }} />
                )}
              </Box>
              <Box
                onClick={() =>
                  downloadingTemplate === null && downloadTemplate(true)
                }
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  border: `1px solid ${brand[100]}`,
                  bgcolor: brand[50],
                  cursor: "pointer",
                  "&:hover": { bgcolor: brand[100] },
                }}
              >
                <Typography variant="subtitle2" color={brand[600]}>
                  Pre-filled template (Recommended)
                </Typography>
                {downloadingTemplate === "prefilled" ? (
                  <CircularProgress size={18} sx={{ color: brand[600] }} />
                ) : (
                  <FileDownloadOutlinedIcon sx={{ color: brand[600] }} />
                )}
              </Box>
            </Stack>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Box
                sx={{
                  backgroundColor: brand[600],
                  borderRadius: "999px",
                  width: 25,
                  height: 25,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography color="#FFFFFF">2</Typography>
              </Box>
              <Stack spacing={1}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Fill your data
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Open the template and fill in or updated the required columns.
                </Typography>
              </Stack>
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Box
                sx={{
                  backgroundColor: brand[600],
                  borderRadius: "999px",
                  width: 25,
                  height: 25,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography color="#FFFFFF">3</Typography>
              </Box>
              <Stack spacing={1}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Upload the CSV file
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use the upload area on the right to submit your file.
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box flex={1}>
          <Typography variant="h4">CSV upload &amp; Processing</Typography>
          <Box mt={2}>
            <CsvUpload
              value={file}
              onChange={handleFileChange}
              confirmReupload={hasRunValidation}
            />
          </Box>
        </Box>
      </Box>
      {file && awaitingValidation && (
        <Box
          sx={{
            px: 3,
            pb: 3,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            disabled={isUploading || isValidating || !jobId}
            endIcon={
              isUploading || isValidating ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : (
                <ArrowForwardIosIcon sx={{ fontSize: "13px !important" }} />
              )
            }
            onClick={() => runValidation()}
            sx={{
              bgcolor: brand[600],
              "&:hover": { bgcolor: brand[700] },
              "&.Mui-disabled": { bgcolor: brand[300], color: "white" },
              borderRadius: "12px",
              p: 2,
              color: "white",
            }}
          >
            {isUploading
              ? "Uploading…"
              : isValidating
                ? "Validating…"
                : "Run validation"}
          </Button>
        </Box>
      )}
    </Box>
  );
};
